import Image from 'next/image';
import React, { useRef, useState } from 'react';

import Button from '@/components/common/Button';
import { Commodity, CommodityCurrency } from '@/components/RightProvider/const';
import { UseTranslateType } from '@/hooks/useTranslate';

interface SingleItemProps {
  data: Commodity[number];
  t: UseTranslateType;
  loading: boolean;
  onClick: (price: string, currency: string) => void;
  position: Position;
}
type Position = 'prev' | 'next' | 'current';

const CreditIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8.90757 5.84393C9.03475 5.35771 9.47551 5 10 5C10.5245 5 10.9652 5.3577 11.0924 5.84392C11.3226 6.7237 11.6907 7.37193 12.1748 7.85091C12.6591 8.33015 13.3142 8.69418 14.2026 8.91956C14.6633 9.03641 15 9.45209 15 9.94446C15 10.501 14.6217 10.9667 14.11 11.1047C13.2464 11.3375 12.6087 11.7054 12.1364 12.1877C11.6636 12.6705 11.3039 13.3218 11.0804 14.2026C10.9636 14.6632 10.5479 15 10.0555 15C9.49904 15 9.03327 14.6217 8.89532 14.11C8.6625 13.2463 8.2946 12.6086 7.81232 12.1364C7.32952 11.6636 6.67822 11.3039 5.79737 11.0804C5.33677 10.9636 5 10.5479 5 10.0555C5 9.49904 5.37833 9.03327 5.89005 8.89532C6.74619 8.66452 7.38014 8.30098 7.85091 7.82525C8.32193 7.34926 8.68139 6.7086 8.90757 5.84393Z"
      fill="#4931FA"
      stroke="#4931FA"
      strokeWidth="0.8"
    />
  </svg>
);
const ArrowIcon: React.FC<React.SVGProps<SVGElement>> = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    fill="none"
    viewBox="0 0 24 24"
    className={props.className}
    onClick={props.onClick}
    style={props.style}
  >
    <path
      stroke="#fff"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="m15 19-7-7 7-7"
    ></path>
  </svg>
);

const SingleItem: React.FC<SingleItemProps> = ({
  data,
  t,
  loading,
  position,
  onClick,
}) => {
  const currency = data?.content?.currency === CommodityCurrency.CNY ? '¥' : '$';
  const coin = data?.content?.point || 0;
  const originalPrice = Number(data?.originalPrice / 100) || 0;
  const realPrice = Number(data?.realPrice / 100) || 0;
  const hasDisCount = originalPrice !== realPrice;
  const DisCountComp = (
    <div className="flex items-center gap-[6px]">
      <div className="text-[#000]">
        <span className="text-[24px] text-[#FDD014]">{currency}</span>
        <span className="leading-[32px]  font-[500] text-[24px] text-[#FDD014]">
          {realPrice}
        </span>
      </div>
      <div className="text-[#000] text-line-through">
        <span className="text-[16px] ">{currency}</span>
        <span className="leading-[32px] font-[500] text-[16px]">{originalPrice}</span>
      </div>
    </div>
  );
  const UnDisCountComp = (
    <div className="text-[#000]">
      <span className="text-[24px]">{currency}</span>
      <span className="leading-[32px] font-[500] text-[24px]">{realPrice}</span>
    </div>
  );
  const Corner = (
    <div
      className="transition absolute right-[0px] top-[0px] px-[20px] z-10 py-[5px] text-[14px] text-[#fff] flex justify-center items-center tracking-[0.5px]"
      style={{
        borderRadius: '0 8px 0 8px',
        background: 'linear-gradient(270deg, #EF5CFF 0%, #4D2FFF 100%)',
      }}
    >
      {t('merror.sub.purchaseBadge')}
    </div>
  );
  const SubmitBtn = () => {
    if (position === 'current') {
      return (
        <Button
          style={{
            background: 'linear-gradient(90deg, #4D2FFF 0%, #EF5CFF 100%)',
          }}
          className={`!w-fit !py-[8px] !px-[28px] ${position !== 'current' ? '!bg-[#385799]' : ''}`}
          loading={loading}
          onClick={() => {
            onClick(String(realPrice), currency);
          }}
        >
          {t('merror.sub.try')}
        </Button>
      );
    } else {
      return (
        <div
          style={{
            width: '100%',
            display: 'inline-block',
            borderRadius: 12,
            padding: 2,
            background: 'linear-gradient(90deg, #4D2FFF, #EF5CFF)',
            boxSizing: 'border-box',
            cursor: 'pointer',
          }}
          onClick={() => {
            onClick(String(realPrice), currency);
          }}
        >
          <div
            style={{
              width: '100%',
              textAlign: 'center',
              borderRadius: 10,
              background: '#ffffff',
              padding: '6px 14px',
              display: 'inline-block',
              fontWeight: 600,
              fontSize: 16,
            }}
          >
            <span
              style={{
                backgroundImage: 'linear-gradient(90deg, #4D2FFF, #EF5CFF)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                color: 'transparent',
              }}
            >
              {t('merror.sub.try')}
            </span>
          </div>
        </div>
      );
    }
  };

  return (
    <div
      className="flex-1 flex-shrink-0 min-w-[284px] h-[286px] rounded-[10px] overflow-hidden relative p-[2px] box-border "
      style={{
        background:
          position === 'current' ? 'linear-gradient(90deg, #4D2FFF, #EF5CFF)' : '#FFFFFF',
      }}
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          borderRadius: 8,
          background: '#ffffff',
        }}
      >
        <div className="flex flex-col items-center z-[2] absolute top-0 left-0 w-full h-full">
          {position === 'current' ? Corner : null}
          <div className="text-payment-modal-card-title-color text-[16px] leading-[22px] pt-[32px]">
            {t('Al-Credits-Package')}
          </div>
          <div className="flex items-center gap-[4px] pt-[14px]">
            <CreditIcon />
            <span className="text-[48px] font-[700] leading-[56px] text-[#4931FA]">
              {coin}
            </span>
          </div>
          <div className="py-[26px]">{hasDisCount ? DisCountComp : UnDisCountComp}</div>
          <div>
            <SubmitBtn />
          </div>
        </div>
      </div>
      {/* 背景 */}
      {/* <div className="absolute w-full h-full z-1 bg-[#fff]"></div> */}
      {/* 内容 */}
    </div>
  );
};

const SingleContent = ({
  data,
  highlightCode,
  loadingStates,
  onSingleClick,
  t,
}: {
  data: Commodity;
  highlightCode: string;
  loadingStates: boolean[];
  onSingleClick: (
    data: Commodity[number],
    btnIndex: number,
    price: string,
    currency: string,
  ) => void;
  t: UseTranslateType;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const currentItem = data?.find((item) => item.commodityCode === highlightCode);
  const [dataScrollPos, setDataScrollPos] = useState<'start' | 'end' | 'middle'>('start');
  const scrollTo = (dis: 'left' | 'right') => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        left: dis === 'left' ? 0 : containerRef.current.scrollWidth,
        behavior: 'smooth',
      });
    }
  };
  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const scrollLeft = target.scrollLeft;
    const scrollWidth = target.scrollWidth;
    const clientWidth = target.clientWidth;

    const isStart = scrollLeft <= 0;
    const isEnd = scrollLeft >= scrollWidth - clientWidth;
    setDataScrollPos(isStart ? 'start' : isEnd ? 'end' : 'middle');
  };
  return (
    <div
      className={`select-none w-full h-[286px] relative ${data.length < 4 ? 'px-[60px]' : 'px-[40px]'}`}
    >
      {data.length > 4 ? (
        <>
          <ArrowIcon
            className={`absolute top-1/2 left-[8px] cursor-pointer ${dataScrollPos === 'start' ? 'hidden' : 'block'}`}
            onClick={() => scrollTo('left')}
          />
          <ArrowIcon
            className={`absolute rotate-180 top-1/2 right-[8px] cursor-pointer ${dataScrollPos === 'end' ? 'hidden' : 'block'}`}
            onClick={() => scrollTo('right')}
          />
        </>
      ) : null}
      <div
        ref={containerRef}
        className="flex gap-[8px] overflow-x-auto no-scrollbar"
        onScroll={onScroll}
      >
        {data.map((item, btnIndex) => {
          const position = !currentItem
            ? 'prev'
            : currentItem.sort > item.sort
              ? 'prev'
              : currentItem.sort < item.sort
                ? 'next'
                : 'current';

          return (
            <SingleItem
              key={btnIndex}
              position={position}
              data={item}
              t={t}
              loading={loadingStates[btnIndex]}
              onClick={(price, currency) => {
                onSingleClick(item, btnIndex, price, currency);
              }}
            />
          );
        })}
      </div>
    </div>
  );
};
export default SingleContent;
